import { Google } from "grommet-icons";
import React from "react";
import { useTranslation } from "react-i18next";
import { useBreakpoint } from "styled-breakpoints/react-styled";
import styled from "styled-components";
import { StringParam, useQueryParam } from "use-query-params";

import { Action } from "containers/Onboarding/components";
import { BackgroundColorYellow } from "ds/Alias";
import { Box } from "ds/Box";
import { ColorNeutralWhite, ColorPurpleDarkest, ColorYellowBase } from "ds/Core";
import { TurnWordMark } from "ds/Logo";
import { Heading } from "ds/Typography";
import { breakpoints, down } from "ds/theme";
import { MicrosoftLogo } from "lib/icons/MicrosoftLogo";
import { calcSpacing } from "lib/impact";
import { TurnHostInfo } from "types";
import { isDevOrPR } from "utils/isDev";

const Ripple = styled.svg`
  position: absolute;
  left: 50%;
  bottom: 0;
  width: 140%;
  aspect-ratio: square;
  z-index: 0;
  transform: translateX(-50%) translateY(50%);

  @media screen and (min-width: ${breakpoints.md}) {
    width: auto;
    right: 0;
    left: auto;
    top: 50%;
    bottom: 0;
    transform: translateX(50%) translateY(-50%);
  }
`;

const LogoWrapper = styled.div`
  svg {
    width: 104px;
  }
`;

export interface ISignIn {
  turnHostInfo: TurnHostInfo;
}
export const SignIn = ({ turnHostInfo }: ISignIn) => {
  const { t } = useTranslation();
  const [referral = ""] = useQueryParam("referral", StringParam);
  const isMobile = useBreakpoint(down("md"));
  const searchParams = new URLSearchParams({
    redirect_to: window.location.href,
    prompt: "login",
    referral,
  });

  const getLoginUrl = (SSOprovider: string) => `${turnHostInfo.turn_host}/auth/${SSOprovider}?${searchParams}`;

  return (
    <Box
      bg={ColorPurpleDarkest}
      minHeight="100vh"
      position="relative"
      p={{ sm: "400", lg: "800" }}
      overflow="hidden"
      display="flex"
      flexDirection="column"
    >
      <Box zIndex="1" position="relative">
        <Box maxWidth="440px">
          <Heading mb="100" level={isMobile ? "h2" : "h1"} color={ColorYellowBase}>
            {t("login.pre-heading")}
          </Heading>
          <Heading fontWeight="300" level={isMobile ? "h2" : "h1"} color={ColorNeutralWhite}>
            {t("login.heading")}
          </Heading>
        </Box>
        {isDevOrPR && (
          <Action
            label={t("login.action-fake")}
            href="/api/account/fake?email=user@example.org"
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" width="24" height="24">
                <path
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 3h1.2c1.68 0 2.52 0 3.162.327a3 3 0 011.311 1.311C21 5.28 21 6.12 21 7.8v8.4c0 1.68 0 2.52-.327 3.162a3 3 0 01-1.311 1.311C18.72 21 17.88 21 16.2 21H15M10 7l5 5m0 0l-5 5m5-5H3"
                />
              </svg>
            }
            outline
            margin={{ top: calcSpacing(["m", "s"]) }}
            style={{ marginBottom: "-24px", justifyItems: "space-between" }}
          />
        )}
        <Action
          label={t("login.action-google")}
          href={getLoginUrl("google")}
          icon={<Google color="plain" />}
          outline
          margin={{ top: calcSpacing(["l", "m"]) }}
        />
        <Action
          label={t("login.action-microsoft")}
          href={getLoginUrl("microsoft")}
          icon={<MicrosoftLogo />}
          outline
          margin={{ top: calcSpacing(["m", "s"]) }}
        />
      </Box>

      {!isMobile && (
        <Box mt="auto" zIndex="1" position="relative">
          <LogoWrapper>
            <TurnWordMark fill={BackgroundColorYellow} />
          </LogoWrapper>
        </Box>
      )}

      <Box>
        {isMobile && (
          <Box mb="200" position="absolute" bottom="0px" left="0px" right="0px" justifySelf="center">
            <LogoWrapper>
              <TurnWordMark fill={BackgroundColorYellow} />
            </LogoWrapper>
          </Box>
        )}
        <Ripple
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1536 1536"
          fill="none"
          style={{ bottom: isMobile ? "-16px" : "0" }}
          opacity={isMobile ? 0.5 : 1}
        >
          <path
            fill={ColorYellowBase}
            d="M768 1536c424.15 0 768-343.85 768-768 0-424.155-343.85-768-768-768C343.845 0 0 343.845 0 768c0 424.15 343.845 768 768 768Z"
            opacity=".3"
          />
          <path
            fill={ColorYellowBase}
            d="M768 1280c282.77 0 512-229.23 512-512s-229.23-512-512-512-512 229.23-512 512 229.23 512 512 512Z"
            opacity=".6"
          />
          <path
            fill={ColorYellowBase}
            d="M768 1024c141.38 0 256-114.615 256-256S909.38 512 768 512c-141.385 0-256 114.615-256 256s114.615 256 256 256Z"
            opacity=".6"
          />
          <path
            fill={ColorYellowBase}
            d="M768 1067.99c165.68 0 299.99-134.311 299.99-299.993S933.68 468.004 768 468.004c-165.682 0-299.993 134.311-299.993 299.993S602.318 1067.99 768 1067.99Z"
          />
        </Ripple>
      </Box>
    </Box>
  );
};
